/* dropdown YWC — DEV-16505 — body-level overlay positioned via floating-ui */
(function ensure_dropdown_css_once() {
	if (!document.querySelector('.js-yalwa_dropdown_shared_css')) {
		const link = document.createElement('link');
		link.className = 'js-yalwa_dropdown_shared_css';
		link.rel = 'stylesheet';
		link.href = yalwa.basics.static_url('css') + 'web_components/dropdown.css';
		document.head.appendChild(link);
	}
})();

// Trigger-to-menu vertical gap (8px in all Figma variants).
const DROPDOWN_GAP = 8;

const DROPDOWN_PLACEMENT_MAP = {
	'bottom-left':  'bottom-start',
	'bottom-right': 'bottom-end',
};

const ICON_ID_CHEVRON_DOWN = 1391;
const ICON_ID_MAGNIFIER    = 1906;
const ICON_ID_CLEAR        = 1517;

let dropdown_current = null;
let doc_listeners_attached = false;

function on_document_click(e) {
	if (dropdown_current && !dropdown_current.contains_related(e.target)) {
		dropdown_current.close();
	}
}

function on_document_keydown(e) {
	if (e.key === 'Escape' && dropdown_current) {
		// With an active search query, the first Escape clears the search
		// input instead of closing the menu (standard search UX).
		if (dropdown_current.search_input && dropdown_current.search_input.value) {
			dropdown_current.search_input.value = '';
			dropdown_current.sync_search_state();
			dropdown_current.search_input.focus();
		} else {
			dropdown_current.close();
		}
		e.stopPropagation();
	}
}

// Document-level listeners are only attached while a dropdown menu is open
// (attached in open(), detached in close()) — no cost when none is open.
function attach_document_listeners() {
	if (doc_listeners_attached) {
		return;
	}
	doc_listeners_attached = true;
	document.addEventListener('click', on_document_click, true);
	document.addEventListener('keydown', on_document_keydown, true);
}

function detach_document_listeners() {
	if (!doc_listeners_attached) {
		return;
	}
	doc_listeners_attached = false;
	document.removeEventListener('click', on_document_click, true);
	document.removeEventListener('keydown', on_document_keydown, true);
}

// The browser's own localized "fill out this field" message, read from a
// throwaway native input. setValidity requires a non-empty message whenever a
// validity flag is set — and we must not hardcode English text.
let value_missing_message_cache = null;

function get_value_missing_message() {
	if (value_missing_message_cache === null) {
		const probe = document.createElement('input');
		probe.required = true;
		value_missing_message_cache = probe.validationMessage;
	}
	return value_missing_message_cache;
}

let float_ui_load_promise = null;

function ensure_floating_ui() {
	if (float_ui_load_promise) {
		return float_ui_load_promise;
	}
	float_ui_load_promise = new Promise(function (resolve) {
		const s = document.createElement('script');
		s.src = yalwa.basics.static_url('js') + 'external/floating-ui/floating-ui.umd.min.js';
		s.onload = function () {
			resolve(window.FloatingUIDOM);
		};
		s.onerror = function () {
			resolve(null);
		};
		document.head.appendChild(s);
	});
	return float_ui_load_promise;
}


class YalwaDropdown extends HTMLElement {

	// Form-associated custom element: participates in <form> submission and
	// validation when a `name` attribute is present (progressive form mode).
	static formAssociated = true;

	static get observedAttributes() {
		return ['placement', 'spacing', 'open', 'no-icon', 'searchable', 'full-width', 'name', 'disabled', 'required'];
	}

	constructor() {
		super();
		this.state = 'closed';
		this.menu = null;
		this.menu_scroll = null;
		this.trigger = null;
		this.items = [];
		this.radio_selected = null;
		this.radio_group_id = 'dd-radio-' + Math.random().toString(36).substr(2, 9);
		this.menu_id = 'yalwa-dropdown-menu-' + Math.random().toString(36).substr(2, 9);
		this.cleanup_pos = null;
		this.search_input = null;
		this.bound_toggle = null;
		this._internals = this.attachInternals ? this.attachInternals() : null;
	}

	connectedCallback() {
		this.resolve_trigger();
		this.read_items();
		this.wrap_trigger_label();
		this.inject_chevron();
		this.bind_events();
		this.upgrade_properties(['name', 'value', 'disabled', 'required']);
		// A parsed `disabled` attribute fires attributeChangedCallback during
		// upgrade — before the trigger exists — so sync it here as well.
		this.sync_disabled();
		this.sync_form_value();
		if (this.hasAttribute('open')) {
			this.open();
		}
		jQuery(this).removeClass('invisible');
	}

	disconnectedCallback() {
		this.unbind_events();
		this.close();
		this.destroy_menu();
	}

	attributeChangedCallback(name, old_value, new_value) {
		if (old_value === new_value) {
			return;
		}
		if (name === 'open' && this.isConnected) {
			if (new_value !== null) {
				if (this.state !== 'open') {
					this.open();
				}
			} else if (this.state !== 'closed') {
				this.close();
			}
		} else if (name === 'disabled') {
			this.sync_disabled();
		} else if (name === 'required') {
			this.update_validity();
		} else if (name === 'name') {
			if (new_value === null) {
				// Leaving form mode — clear the published value AND any stale
				// validity state (it would never be recomputed without a name).
				if (this._internals) {
					this._internals.setFormValue(null);
					this._internals.setValidity({});
				}
			} else {
				this.sync_form_value();
			}
		}
	}

	// --- Trigger ---

	resolve_trigger() {
		let node = this.firstChild;
		while (node) {
			if (node.nodeType === Node.ELEMENT_NODE) {
				this.trigger = node;
				return;
			}
			node = node.nextSibling;
		}
		this.trigger = null;
	}

	is_trigger(el) {
		return this.trigger && (el === this.trigger || this.trigger.contains(el));
	}

	// --- Trigger label truncation ---

	// The trigger label is a bare text node, which the stylesheet cannot
	// target. With `truncate-trigger` the text is wrapped so it can ellipsize
	// instead of wrapping to a second line in a narrow trigger container.
	wrap_trigger_label() {
		if (!this.trigger || !this.hasAttribute('truncate-trigger')) {
			return;
		}
		const label_class = 'yalwa_dropdown__trigger_label';
		if (this.trigger.querySelector(':scope > .' + label_class)) {
			return;
		}
		const text_nodes = [];
		this.trigger.childNodes.forEach(function (node) {
			if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '') {
				text_nodes.push(node);
			}
		});
		if (!text_nodes.length) {
			return;
		}
		const label = document.createElement('span');
		label.className = label_class;
		this.trigger.insertBefore(label, text_nodes[0]);
		text_nodes.forEach(function (node) {
			label.appendChild(node);
		});
	}

	// --- Chevron injection ---

	inject_chevron() {
		if (!this.trigger || this.hasAttribute('no-icon')) {
			return;
		}
		if (!this.trigger.tagName || this.trigger.tagName.toLowerCase() !== 'yalwa-button') {
			return;
		}
		if (this.trigger.querySelector('[slot="icon_right"]')) {
			return;
		}
		const wrapper = document.createElement('span');
		wrapper.setAttribute('slot', 'icon_right');
		wrapper.className = 'yalwa_dropdown__trigger_chevron';
		wrapper.appendChild(this.create_yimage_icon(ICON_ID_CHEVRON_DOWN, 24, ''));
		this.trigger.appendChild(wrapper);
	}

	// --- Items ---

	read_items() {
		this.items = [];
		this.radio_selected = null;
		const item_els = this.querySelectorAll(':scope > yalwa-dropdown-item');
		const self = this;
		item_els.forEach(function (item_el) {
			self.items.push(self.read_item_data(item_el));
		});
	}

	read_item_data(item_el) {
		const appearance = item_el.getAttribute('appearance') || 'simple';
		const selected = item_el.hasAttribute('selected');
		const value = item_el.getAttribute('value');
		const data = {
			element: item_el,
			label: item_el.getAttribute('label'),
			description: item_el.getAttribute('description'),
			initials: item_el.getAttribute('initials'),
			value: value,
			appearance: appearance,
			selected: selected,
			disabled: item_el.hasAttribute('disabled'),
			divider: item_el.hasAttribute('divider'),
			icon_before_uid: item_el.getAttribute('icon-before'),
			icon_after_uid: item_el.getAttribute('icon-after'),
		};
		if (selected && appearance === 'radio') {
			this.radio_selected = value;
		}
		return data;
	}

	// --- Events ---

	bind_events() {
		if (!this.trigger) {
			return;
		}
		this.bound_toggle = this.toggle.bind(this);
		this.bound_trigger_keydown = this.handle_trigger_keydown.bind(this);
		this.trigger.addEventListener('click', this.bound_toggle);
		this.trigger.addEventListener('keydown', this.bound_trigger_keydown);

		// Initialize trigger ARIA attributes on connection
		this.trigger.setAttribute('aria-haspopup', 'menu');
		this.trigger.setAttribute('aria-controls', this.menu_id);
		this.trigger.setAttribute('aria-expanded', this.state === 'open' ? 'true' : 'false');
	}

	unbind_events() {
		if (this.trigger) {
			if (this.bound_toggle) {
				this.trigger.removeEventListener('click', this.bound_toggle);
			}
			if (this.bound_trigger_keydown) {
				this.trigger.removeEventListener('keydown', this.bound_trigger_keydown);
			}
		}
	}

	handle_trigger_keydown(e) {
		if (this.disabled) {
			return;
		}
		if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || (e.altKey && e.key === 'ArrowDown')) {
			e.preventDefault();
			if (this.state !== 'open') {
				this.open().then(() => {
					if (e.key === 'ArrowUp') {
						this.focus_last_item();
					} else {
						this.focus_first_item();
					}
				});
			}
		}
	}

	toggle(event) {
		event.stopPropagation();
		if (this.disabled) {
			return;
		}
		if (this.state === 'open') {
			this.close();
		} else {
			this.open();
		}
	}

	contains_related(el) {
		if (!el) {
			return false;
		}
		return (this.menu && this.menu.contains(el)) || this.is_trigger(el) || this.contains(el);
	}

	// --- Open / Close ---

	async open() {
		if (this.state === 'open') {
			return;
		}
		if (!this.trigger) {
			this.resolve_trigger();
		}
		if (!this.items.length) {
			this.read_items();
		}
		if (dropdown_current && dropdown_current !== this) {
			dropdown_current.close();
		}
		this.ensure_menu();
		this.state = 'open';
		if (!this.hasAttribute('open')) {
			this.setAttribute('open', '');
		}
		dropdown_current = this;
		attach_document_listeners();

		const floating = await ensure_floating_ui();
		if (this.state !== 'open') {
			return;
		}
		if (!floating) {
			return;
		}

		if (this.trigger) {
			this.trigger.setAttribute('data-dropdown-open', '');
			this.trigger.setAttribute('aria-expanded', 'true');
		}
		this.menu.classList.add('is_visible');
		this.do_position(floating);
		// The menu opens in its default (unfocused-input) state per Figma — focus
		// goes to the first item (keyboard navigation), not into the search field.
		this.focus_first_item();
	}

	close() {
		if (this.state === 'closed') {
			return;
		}
		const menu_has_focus = this.menu && this.menu.contains(document.activeElement);
		if (this.menu) {
			this.menu.classList.remove('is_visible');
		}
		if (menu_has_focus && this.trigger && this.trigger.focus) {
			this.trigger.focus({preventScroll: true});
		}
		if (this.trigger) {
			this.trigger.removeAttribute('data-dropdown-open');
			this.trigger.setAttribute('aria-expanded', 'false');
		}
		this.stop_positioning();
		if (dropdown_current === this) {
			dropdown_current = null;
			detach_document_listeners();
		}
		this.state = 'closed';
		if (this.hasAttribute('open')) {
			this.removeAttribute('open');
		}
	}

	// --- Menu DOM construction ---

	ensure_menu() {
		if (this.menu) {
			return;
		}
		const spacing = this.getAttribute('spacing') || 'm';

		this.menu = document.createElement('div');
		this.menu.className = 'yalwa_dropdown__menu';
		this.menu.setAttribute('data-spacing', spacing);
		this.menu.id = this.menu_id;
		// WAI-ARIA menu pattern: the overlay is the menu, announced via the
		// trigger's accessible name (aria-labelledby resolves across shadow
		// boundaries through the button host).
		this.menu.setAttribute('role', 'menu');
		if (this.trigger && this.trigger.id) {
			this.menu.setAttribute('aria-labelledby', this.trigger.id);
		} else if (this.getAttribute('menu-label')) {
			this.menu.setAttribute('aria-label', this.getAttribute('menu-label'));
		} else if (this.trigger) {
			// Fall back to the trigger's visible text as the menu's accessible name.
			const trigger_text = (this.trigger.textContent || '').trim();
			if (trigger_text) {
				this.menu.setAttribute('aria-label', trigger_text);
			}
		}

		if (this.trigger) {
			// Menu-button pattern: announce popup + expanded state + control
			// target on the trigger (yalwa-button forwards host ARIA attrs into
			// its inner <button> via button_base).
			this.trigger.setAttribute('aria-haspopup', 'menu');
			this.trigger.setAttribute('aria-controls', this.menu_id);
			this.trigger.setAttribute('aria-expanded', 'false');
		}
		if (this.hasAttribute('searchable')) {
			const search_wrap = document.createElement('div');
			search_wrap.className = 'yalwa_dropdown__menu_search';
			search_wrap.setAttribute('data-spacing', spacing);

			const magnifier = document.createElement('span');
			magnifier.className = 'yalwa_dropdown__search_icon';
			magnifier.setAttribute('data-spacing', spacing);
			magnifier.appendChild(this.create_yimage_icon(ICON_ID_MAGNIFIER, 24, 'yalwa_dropdown__search_icon_img'));
			search_wrap.appendChild(magnifier);

			const search_input = document.createElement('input');
			search_input.className = 'yalwa_dropdown__menu_search_input';
			search_input.type = 'search';
			// Labels come from attributes (the PHP builder supplies translated
			// values via label::) — nothing hardcoded here.
			if (this.getAttribute('search-placeholder')) {
				search_input.placeholder = this.getAttribute('search-placeholder');
			}
			if (this.getAttribute('search-label')) {
				search_input.setAttribute('aria-label', this.getAttribute('search-label'));
			}
			search_input.setAttribute('data-spacing', spacing);
			search_wrap.appendChild(search_input);

			const clear_btn = document.createElement('button');
			clear_btn.className = 'yalwa_dropdown__search_clear';
			clear_btn.type = 'button';
			if (this.getAttribute('clear-label')) {
				clear_btn.setAttribute('aria-label', this.getAttribute('clear-label'));
			}
			clear_btn.appendChild(this.create_yimage_icon(ICON_ID_CLEAR, 20, 'yalwa_dropdown__search_clear_img'));
			search_wrap.appendChild(clear_btn);

			// Separator under the search field — nested LDS <yalwa-divider>
			// (appearance light = Figma stroke rgba(0,0,0,0.08)), pinned to the
			// bottom of the search area via dropdown.scss, same as item dividers.
			const search_divider = document.createElement('yalwa-divider');
			search_divider.setAttribute('appearance', 'light');
			search_wrap.appendChild(search_divider);

			this.menu.appendChild(search_wrap);
			this.search_input = search_input;

			const self = this;
			search_input.addEventListener('input', function () {
				self.sync_search_state();
			});
			clear_btn.addEventListener('click', function () {
				search_input.value = '';
				self.sync_search_state();
				search_input.focus();
			});
		}

		this.menu_scroll = document.createElement('div');
		this.menu_scroll.className = 'yalwa_dropdown__menu_scroll';

		const self = this;
		this.items.forEach(function (item_data, index) {
			const row = self.build_item_row(item_data, index, spacing);
			self.menu_scroll.appendChild(row);
		});

		this.menu.appendChild(this.menu_scroll);
		document.body.appendChild(this.menu);

		this.sync_item_display();

		this.menu.addEventListener('keydown', function (e) {
			self.handle_menu_keydown(e);
		});

		requestAnimationFrame(function () {
			self.update_scroll_limit();
		});
	}

	handle_menu_keydown(e) {
		if (this.state !== 'open' || !this.menu_scroll) {
			return;
		}
		const rows = Array.from(this.menu_scroll.querySelectorAll('.js-dropdown_item'));
		const enabled = rows.filter(function (row) {
			return row.style.display !== 'none' && row.dataset.disabled !== 'true';
		});
		if (!enabled.length && document.activeElement !== this.search_input) {
			return;
		}
		const current = rows.indexOf(document.activeElement);

		switch (e.key) {
			case 'ArrowDown': {
				e.preventDefault();
				if (this.search_input && document.activeElement === this.search_input) {
					if (enabled.length) {
						this.focus_row(enabled[0]);
					}
				} else {
					const active_idx = enabled.indexOf(document.activeElement);
					const next = enabled[(active_idx + 1) % enabled.length] || enabled[0];
					if (next) {
						this.focus_row(next);
					}
				}
				break;
			}
			case 'ArrowUp': {
				e.preventDefault();
				if (this.search_input && document.activeElement === this.search_input) {
					if (enabled.length) {
						this.focus_row(enabled[enabled.length - 1]);
					}
				} else {
					const active_idx = enabled.indexOf(document.activeElement);
					const prev = enabled[(active_idx - 1 + enabled.length) % enabled.length] || enabled[enabled.length - 1];
					if (prev) {
						this.focus_row(prev);
					}
				}
				break;
			}
			case 'Home': {
				if (document.activeElement !== this.search_input) {
					e.preventDefault();
					if (enabled.length) {
						this.focus_row(enabled[0]);
					}
				}
				break;
			}
			case 'End': {
				if (document.activeElement !== this.search_input) {
					e.preventDefault();
					if (enabled.length) {
						this.focus_row(enabled[enabled.length - 1]);
					}
				}
				break;
			}
			case 'Enter': {
				if (current >= 0) {
					e.preventDefault();
					const nested = rows[current].querySelector('yalwa-checkbox, yalwa-radio-option');
					if (nested) {
						nested.click();
					} else {
						this.handle_item_click(current);
					}
				} else if (this.search_input && document.activeElement === this.search_input) {
					if (enabled.length) {
						e.preventDefault();
						const index = rows.indexOf(enabled[0]);
						const nested = enabled[0].querySelector('yalwa-checkbox, yalwa-radio-option');
						if (nested) {
							nested.click();
						} else {
							this.handle_item_click(index);
						}
					}
				}
				break;
			}
			case ' ': {
				// Space activates focused item row; space in search input is normal text typing
				if (current >= 0) {
					e.preventDefault();
					const nested = rows[current].querySelector('yalwa-checkbox, yalwa-radio-option');
					if (nested) {
						nested.click();
					} else {
						this.handle_item_click(current);
					}
				}
				break;
			}
			case 'Tab': {
				this.close();
				break;
			}
		}
	}

	// Focus a row without scrolling the page. Visibility inside the menu is
	// handled by scrolling the menu's own scroll container (scrollIntoView would
	// also scroll the page, which causes jumps while the menu's transform is
	// applied asynchronously by floating-ui).
	focus_row(row) {
		if (!row || !this.menu_scroll) {
			return;
		}
		// Roving tabindex: the focused row is the only tabbable one; the rest
		// stay tabindex="-1" (WAI-ARIA menu pattern).
		const all_rows = this.menu_scroll.querySelectorAll('.js-dropdown_item');
		for (let i = 0; i < all_rows.length; i++) {
			all_rows[i].setAttribute('tabindex', '-1');
		}
		row.setAttribute('tabindex', '0');
		row.focus({preventScroll: true});
		const container = this.menu_scroll;
		const row_top = row.offsetTop - container.offsetTop;
		const row_bottom = row_top + row.offsetHeight;
		if (row_top < container.scrollTop) {
			container.scrollTop = row_top;
		} else if (row_bottom > container.scrollTop + container.clientHeight) {
			container.scrollTop = row_bottom - container.clientHeight;
		}
	}

	focus_first_item() {
		if (!this.menu_scroll) {
			return;
		}
		const rows = Array.from(this.menu_scroll.querySelectorAll('.js-dropdown_item'));
		const enabled = rows.filter(function (row) {
			return row.style.display !== 'none' && row.dataset.disabled !== 'true';
		});
		if (enabled.length) {
			this.focus_row(enabled[0]);
		}
	}

	focus_last_item() {
		if (!this.menu_scroll) {
			return;
		}
		const rows = Array.from(this.menu_scroll.querySelectorAll('.js-dropdown_item'));
		const enabled = rows.filter(function (row) {
			return row.style.display !== 'none' && row.dataset.disabled !== 'true';
		});
		if (enabled.length) {
			this.focus_row(enabled[enabled.length - 1]);
		}
	}

	update_scroll_limit() {
		if (!this.menu_scroll) {
			return;
		}
		const rows = this.menu_scroll.querySelectorAll('.js-dropdown_item');
		if (rows.length <= 6) {
			return;
		}
		let measured = 0;
		const limit = Math.min(6, rows.length);
		for (let i = 0; i < limit; i++) {
			measured += rows[i].offsetHeight;
		}
		this.menu_scroll.style.maxHeight = measured + 'px';
	}

	filter_items(query) {
		if (!this.menu_scroll) {
			return;
		}
		const q = query.toLowerCase().trim();
		const rows = Array.from(this.menu_scroll.querySelectorAll('.js-dropdown_item'));
		const self = this;
		let first_visible_enabled = null;
		let has_focused_visible = false;

		rows.forEach(function (row, index) {
			const item = self.items[index];
			if (!item) {
				return;
			}
			const label = (item.label || '').toLowerCase();
			const desc = (item.description || '').toLowerCase();
			const match = !q || label.indexOf(q) !== -1 || desc.indexOf(q) !== -1;
			row.style.display = match ? '' : 'none';

			if (match && row.dataset.disabled !== 'true') {
				if (!first_visible_enabled) {
					first_visible_enabled = row;
				}
				if (row.getAttribute('tabindex') === '0') {
					has_focused_visible = true;
				}
			}
		});

		// Ensure one visible enabled row has tabindex="0" for roving tabindex
		if (!has_focused_visible && first_visible_enabled) {
			for (let i = 0; i < rows.length; i++) {
				rows[i].setAttribute('tabindex', '-1');
			}
			first_visible_enabled.setAttribute('tabindex', '0');
		}
	}

	// Keeps the search chrome in sync: clear button visibility and live filtering.
	sync_search_state() {
		if (!this.search_input || !this.menu) {
			return;
		}
		const has_value = this.search_input.value.length > 0;
		// The clear button defaults to display:none in CSS, so it needs an explicit
		// value (not an empty string, which would fall back to the stylesheet).
		this.menu.querySelector('.yalwa_dropdown__search_clear').classList.toggle('is_visible', has_value);
		this.filter_items(this.search_input.value);
	}

	get_item_row(index) {
		if (!this.menu_scroll) {
			return null;
		}
		return this.menu_scroll.querySelector('.js-dropdown_item[data-index="' + index + '"]');
	}

	// --- Item row builder ---

	// Resolve an icon reference to a usable src URL.
	// 1. Full URL ('/' or 'http'): used directly (emitted by PHP ywc_dropdownitem).
	// 2. Numeric UID (e.g. 1178): resolved below to the yimage asset path.
	// 3. Named string: falls back to legacy static icons path.
	resolve_icon_src(icon_ref) {
		if (!icon_ref) {
			return '';
		}
		icon_ref = String(icon_ref);
		if (icon_ref.indexOf('/') === 0 || icon_ref.indexOf('http') === 0) {
			return icon_ref;
		}
		const numeric_id = parseInt(icon_ref, 10);
		if (!isNaN(numeric_id) && String(numeric_id) === String(icon_ref).trim()) {
			const folder = String(numeric_id % 100).padStart(2, '0');
			return yalwa.basics.static_url('images') + 'yimage/' + folder + '/' + numeric_id + '.svg';
		}
		return yalwa.basics.static_url('images') + 'icons/' + icon_ref + '.svg';
	}

	// Build a decorative <img> for a numeric yimage UID via the DOM API rather
	// than innerHTML — no markup parsing, so no injection sink. resolve_icon_src
	// also zero-pads the folder (yimage stores icons in /yimage/06/, not /6/).
	create_yimage_icon(uid, pixel, classes) {
		const img = document.createElement('img');
		img.src = this.resolve_icon_src(String(uid));
		img.alt = '';
		img.width = pixel;
		img.height = pixel;
		img.className = classes ? 'yimage yicon ' + classes : 'yimage yicon';
		return img;
	}

	build_item_row(item_data, index, spacing) {
		const row = document.createElement('div');
		row.className = 'yalwa_dropdown__item js-dropdown_item';
		row.dataset.index = String(index);
		row.dataset.appearance = item_data.appearance;
		row.dataset.spacing = spacing;
		row.setAttribute('tabindex', '-1');

		// WAI-ARIA menu pattern: menuitemcheckbox / menuitemradio / menuitem
		// roles reflect the item appearance; explicit tabindex="-1" makes the
		// row keyboard-focusable while floating-ui positions asynchronously
		// (roving tabindex moves to the active row in focus_row).
		if (item_data.appearance === 'checkbox') {
			row.setAttribute('role', 'menuitemcheckbox');
		} else if (item_data.appearance === 'radio') {
			row.setAttribute('role', 'menuitemradio');
		} else {
			row.setAttribute('role', 'menuitem');
		}

		if (item_data.selected) {
			row.setAttribute('aria-checked', 'true');
		} else if (item_data.appearance === 'checkbox' || item_data.appearance === 'radio') {
			row.setAttribute('aria-checked', 'false');
		}

		if (item_data.disabled) {
			row.dataset.disabled = 'true';
			row.setAttribute('aria-disabled', 'true');
		}

		// Indicator — nest existing YWCs for checkbox, radio, avatar.
		// The nested component handles its own interaction state.
		// The row's click handler fires after and reads the final state.
		if (item_data.appearance === 'checkbox') {
			const cb = document.createElement('yalwa-checkbox');
			// Keep focus on the row, not the nested component.
			cb.setAttribute('tabindex', '-1');
			cb.setAttribute('value', item_data.value || '');
			if (item_data.selected) {
				cb.setAttribute('checked', '');
			}
			row.appendChild(cb);

		} else if (item_data.appearance === 'radio') {
			const rb = document.createElement('yalwa-radio-option');
			// Keep focus on the row, not the nested component.
			rb.setAttribute('tabindex', '-1');
			rb.setAttribute('group-id', this.radio_group_id);
			// The nested radio needs its value — without it, toggling writes
			// 'null' into RADIO_GROUP_STATE, which the proxy treats as clearing,
			// and every option sees value === null and checks itself.
			rb.setAttribute('value', item_data.value || '');
			if (item_data.selected) {
				rb.setAttribute('checked', '');
			}
			row.appendChild(rb);

		} else if (item_data.appearance === 'avatar') {
			const av = document.createElement('yalwa-avatar');
			av.setAttribute('size', 'xs');
			if (item_data.icon_before_uid) {
				av.setAttribute('appearance', 'image');
				av.setAttribute('src', this.resolve_icon_src(item_data.icon_before_uid));
			} else if (item_data.initials) {
				av.setAttribute('appearance', 'initials');
				// textContent, not innerHTML — initials are data, not markup.
				const initials_span = document.createElement('span');
				initials_span.setAttribute('slot', 'initials');
				initials_span.textContent = item_data.initials;
				av.appendChild(initials_span);
			} else {
				av.setAttribute('appearance', 'placeholder');
			}
			row.appendChild(av);

		} else if (item_data.icon_before_uid) {
			const icon = document.createElement('span');
			icon.className = 'yalwa_dropdown__item_icon';
			const img = document.createElement('img');
			img.src = this.resolve_icon_src(item_data.icon_before_uid);
			img.alt = '';
			icon.appendChild(img);
			row.appendChild(icon);
		}

		// Content (label + description)
		const content = document.createElement('div');
		content.className = 'yalwa_dropdown__item_content';

		const label = document.createElement('span');
		label.className = 'yalwa_dropdown__item_label';
		label.textContent = item_data.label || '';
		content.appendChild(label);

		// Description (designer decision: supported for all four appearances)
		if (item_data.description) {
			const desc = document.createElement('span');
			desc.className = 'yalwa_dropdown__item_desc';
			desc.textContent = item_data.description;
			content.appendChild(desc);
		}

		row.appendChild(content);

		// Icon-after (simple items only — checkbox/radio/avatar don't use it)
		if (item_data.appearance !== 'checkbox' && item_data.appearance !== 'radio' && item_data.appearance !== 'avatar' && item_data.icon_after_uid) {
			const icon_after = document.createElement('span');
			icon_after.className = 'yalwa_dropdown__item_icon';
			const img_after = document.createElement('img');
			img_after.src = this.resolve_icon_src(item_data.icon_after_uid);
			img_after.alt = '';
			icon_after.appendChild(img_after);
			row.appendChild(icon_after);
		}

		// Divider — nested LDS <yalwa-divider> (appearance light = Figma stroke
		// rgba(0,0,0,0.08)), absolutely pinned to the item bottom via dropdown.scss.
		if (item_data.divider) {
			const divider = document.createElement('yalwa-divider');
			divider.setAttribute('appearance', 'light');
			row.appendChild(divider);
		}

		// Click handler on the row
		const self = this;
		row.addEventListener('click', function (e) {
			self.handle_item_click(index, e);
		});
		// Prevent mouse down from moving focus into the nested checkbox/radio.
		row.addEventListener('mousedown', function (e) {
			e.preventDefault();
		});

		return row;
	}

	// --- Form association (active when a `name` attribute is present) ---

	upgrade_properties(props) {
		props.forEach(prop => {
			if (this.hasOwnProperty(prop)) {
				const value = this[prop];
				delete this[prop];
				this[prop] = value;
			}
		});
	}

	get form() {
		return this._internals ? this._internals.form : null;
	}

	get name() {
		return this.getAttribute('name');
	}

	set name(value) {
		if (value === null || value === '') {
			this.removeAttribute('name');
		} else {
			this.setAttribute('name', value);
		}
	}

	get disabled() {
		return this.hasAttribute('disabled');
	}

	set disabled(value) {
		if (value) {
			this.setAttribute('disabled', '');
		} else {
			this.removeAttribute('disabled');
		}
	}

	get required() {
		return this.hasAttribute('required');
	}

	set required(value) {
		if (value) {
			this.setAttribute('required', '');
		} else {
			this.removeAttribute('required');
		}
	}

	get value() {
		return this.collect_selected_values().join(',');
	}

	// Accepts a comma-separated string ('a,b') or an array of values. Matching
	// items become selected; all others are deselected (single-select appearances
	// end up with the first/only matching item, checkboxes toggle individually).
	set value(input) {
		if (Array.isArray(input)) {
			this.apply_selected_values(input.map(String));
		} else {
			this.apply_selected_values(String(input ?? '').split(','));
		}
		this.sync_nested_checked();
		this.sync_item_display();
		this.sync_form_value();
	}

	// Values of all currently selected items, in item order. Checkbox/radio
	// states are read from the nested components when the menu is built.
	collect_selected_values() {
		const rows = this.menu_scroll ? this.menu_scroll.querySelectorAll('.js-dropdown_item') : null;
		const values = [];
		const self = this;
		this.items.forEach(function (item, index) {
			let selected = item.selected;
			if (rows && rows[index]) {
				const nested = rows[index].querySelector('yalwa-checkbox, yalwa-radio-option');
				if (nested) {
					selected = nested.hasAttribute('checked');
				}
			}
			if (selected) {
				// Fall back to the label only when it is unambiguous — comma
				// labels would corrupt the comma-joined submitted string.
				if (item.value) {
					values.push(item.value);
				} else if (item.label && item.label.indexOf(',') === -1) {
					values.push(item.label);
				}
			}
		});
		return values;
	}

	apply_selected_values(wanted) {
		const wanted_trimmed = wanted.map(function (v) {
			return v.trim();
		}).filter(function (v) {
			return v !== '';
		});
		this.radio_selected = null;
		this.items.forEach(function (item) {
			const item_value = item.value || item.label || '';
			if (item.appearance === 'radio') {
				// First matching radio wins — a radio group is single-select.
				item.selected = !this.radio_selected && wanted_trimmed.indexOf(item_value) !== -1;
				if (item.selected) {
					this.radio_selected = item_value;
				}
			} else {
				item.selected = wanted_trimmed.indexOf(item_value) !== -1;
			}
		}, this);
	}

	// Mirror the internal selection state into the nested checkbox/radio attrs.
	sync_nested_checked() {
		if (!this.menu_scroll) {
			return;
		}
		const rows = this.menu_scroll.querySelectorAll('.js-dropdown_item');
		const self = this;
		rows.forEach(function (row, index) {
			const item = self.items[index];
			if (!item) {
				return;
			}
			const nested = row.querySelector('yalwa-checkbox, yalwa-radio-option');
			if (!nested) {
				return;
			}
			if (item.selected) {
				nested.setAttribute('checked', '');
			} else {
				nested.removeAttribute('checked');
			}
		});
	}

	sync_disabled(force_disabled) {
		const disabled = force_disabled !== undefined ? force_disabled : this.disabled;
		if (disabled && this.state === 'open') {
			this.close();
		}
		if (this.trigger && this.trigger.tagName && this.trigger.tagName.toLowerCase() === 'yalwa-button') {
			if (disabled) {
				this.trigger.setAttribute('disabled', '');
			} else if (!this.disabled) {
				this.trigger.removeAttribute('disabled');
			}
		}
	}

	// Publishes the current selection to the form via ElementInternals — only
	// when a `name` attribute is present (progressive form mode).
	sync_form_value() {
		if (!this._internals || !this.hasAttribute('name')) {
			return;
		}
		const value = this.collect_selected_values().join(',');
		this._internals.setFormValue(value);
		this.update_validity(value);
	}

	// Native-style validation: `required` + empty value blocks form submission.
	// The message comes from the `validation-message` attribute (translated by
	// the PHP builder); without it the form still blocks but shows no bubble.
	// Gated on `name`: a required dropdown outside form mode must not poison
	// constraint validation (validity would never be recomputed there).
	update_validity(value) {
		if (!this._internals || !this.hasAttribute('name')) {
			return;
		}
		if (value === undefined) {
			value = this.collect_selected_values().join(',');
		}
		if (this.hasAttribute('required') && !value) {
			const message = this.getAttribute('validation-message') || get_value_missing_message();
			this._internals.setValidity({valueMissing: true}, message);
		} else {
			this._internals.setValidity({});
		}
	}

	formResetCallback() {
		// Re-read the light-DOM items — their `selected` attributes never change,
		// so they always carry the initial state. Keep the menu DOM (destroying
		// it would strip the trigger's ARIA relations) and just re-sync it.
		if (this.state !== 'closed') {
			this.close();
		}
		this.read_items();
		this.sync_nested_checked();
		this.sync_item_display();
		this.sync_form_value();
	}

	// <fieldset disabled> propagation — the spec routes fieldset-driven
	// disabling through this callback for form-associated elements. The flag
	// reflects the fieldset's state, so only clear the trigger's disabled
	// attribute when the element has no `disabled` attribute of its own.
	formDisabledCallback(disabled) {
		this.sync_disabled(disabled);
	}

	// Browser back-navigation / session restore: the form re-submits the last
	// posted value so the dropdown comes back in the state the user left it.
	formStateRestoreCallback(state, mode) {
		// An empty string is a valid state too (required dropdown restored empty).
		if (typeof state === 'string') {
			this.value = state;
		}
	}

	// Label-for activation (and programmatic callers) — focus lands on the
	// trigger's inner button, same forwarding yalwa-button does.
	focus(options) {
		if (this.trigger && typeof this.trigger.focus === 'function') {
			this.trigger.focus(options);
		} else {
			super.focus(options);
		}
	}

	// Constraint-validation API on the host — Chrome only exposes it on
	// ElementInternals, so delegate the standard surface for consumers.
	get willValidate() {
		return Boolean(this._internals && this._internals.willValidate);
	}

	get validity() {
		return this._internals ? this._internals.validity : null;
	}

	get validationMessage() {
		return this._internals ? this._internals.validationMessage : '';
	}

	checkValidity() {
		return this._internals ? this._internals.checkValidity() : true;
	}

	reportValidity() {
		return this._internals ? this._internals.reportValidity() : true;
	}

	// --- Item click / selection ---

	handle_item_click(index, event) {
		const item = this.items[index];
		if (!item || item.disabled) {
			return;
		}

		const row = this.get_item_row(index);

		if (item.appearance === 'checkbox') {
			// The nested checkbox toggles itself only when the click lands on it.
			// Clicking anywhere else on the row must toggle it too.
			const cb = row.querySelector('yalwa-checkbox');
			if (cb && !(event && cb.contains(event.target))) {
				cb.toggle();
			}
			const is_checked = cb && cb.hasAttribute('checked');
			item.selected = is_checked;
			// Stay open — user may select multiple

		} else if (item.appearance === 'radio') {
			// The nested radio selects itself only when the click lands on it.
			// Clicking anywhere else on the row must select it too.
			const rb = row.querySelector('yalwa-radio-option');
			if (rb && !(event && rb.contains(event.target))) {
				rb.toggle();
			}
			// Radio system already deselected other options via shared group-id.
			// Read the result from the clicked item.
			const is_checked = rb && rb.hasAttribute('checked');
			if (is_checked) {
				this.radio_selected = item.value;
				for (let i = 0; i < this.items.length; i++) {
					if (this.items[i].appearance === 'radio') {
						this.items[i].selected = (i === index);
					}
				}
			}
			this.close();

		} else if (item.appearance === 'avatar') {
			// Simple single-select with close
			for (let i = 0; i < this.items.length; i++) {
				this.items[i].selected = (i === index);
			}
			this.close();

		} else {
			// Simple appearance — single-select, close
			for (let i = 0; i < this.items.length; i++) {
				this.items[i].selected = (i === index);
			}
			this.close();
		}

		this.sync_item_display();
		this.sync_form_value();
		this.dispatchEvent(new Event('input', {bubbles: true, composed: true}));
		this.dispatchEvent(new CustomEvent('change', {
			detail: {
				value: item.value,
				label: item.label,
				selected: item.selected,
				appearance: item.appearance,
			},
			bubbles: true,
			composed: true,
		}));
	}

	sync_item_display() {
		if (!this.menu_scroll) {
			return;
		}
		const rows = this.menu_scroll.querySelectorAll('.js-dropdown_item');
		const self = this;
		rows.forEach(function (row, index) {
			const item = self.items[index];
			if (!item) {
				return;
			}
		// The row background follows the selection state for every appearance.
		// Checkbox and radio items keep their checked state in the nested
		// component, so read it from there.
		let selected = item.selected;
		if (item.appearance === 'checkbox' || item.appearance === 'radio') {
			const nested = row.querySelector('yalwa-checkbox, yalwa-radio-option');
			if (nested) {
				selected = nested.hasAttribute('checked');
			}
		}
		if (selected) {
			row.dataset.state = 'selected';
		} else {
			delete row.dataset.state;
		}
		// Mirror the selection into the ARIA checked state for checkbox and radio only.
		if (item.appearance === 'checkbox' || item.appearance === 'radio') {
			row.setAttribute('aria-checked', selected ? 'true' : 'false');
		} else {
			row.removeAttribute('aria-checked');
		}
	});
	}

	// --- Positioning (floating-ui) ---

	stop_positioning() {
		if (this.cleanup_pos) {
			this.cleanup_pos();
			this.cleanup_pos = null;
		}
	}

	do_position(floating) {
		if (!floating || !this.menu || !this.trigger || this.state !== 'open') {
			return;
		}
		const placement_value = this.getAttribute('placement') || 'bottom-left';
		const placement = DROPDOWN_PLACEMENT_MAP[placement_value] || 'bottom-start';

		// The trigger-to-menu gap is applied manually in apply_position (data.y + gap),
		// so no floating.offset middleware here — using both would double the gap.
		const middleware = [
			floating.shift({padding: 8}),
		];

		const self = this;
		if (this.cleanup_pos) {
			this.cleanup_pos();
		}
		this.cleanup_pos = floating.autoUpdate(this.trigger, this.menu, function () {
			self.update_position(floating, placement, middleware);
		});
		this.update_position(floating, placement, middleware);
	}

	update_position(floating, placement, middleware) {
		if (!floating) {
			floating = window.FloatingUIDOM;
		}
		if (!floating || !this.menu || !this.trigger || this.state !== 'open') {
			return;
		}
		const self = this;
		floating.computePosition(this.trigger, this.menu, {
			placement: placement,
			middleware: middleware,
		}).then(function (data) {
			self.apply_position(data);
		}).catch(function () {});
	}

	apply_position(data) {
		if (!this.menu) {
			return;
		}
		const style = this.menu.style;
		style.left = '0';
		style.top = '0';
		// Menu width locked to the trigger width: `full-width` also stretches
		// the dropdown itself, `menu-full-width` only matches the menu. Both
		// re-apply on every reposition (autoUpdate also fires on window resize).
		if (this.hasAttribute('full-width') || this.hasAttribute('menu-full-width')) {
			style.minWidth = this.trigger.offsetWidth + 'px';
			style.maxWidth = this.trigger.offsetWidth + 'px';
		}
		style.transform = 'translate(' + Math.round(data.x) + 'px, ' + Math.round(data.y + DROPDOWN_GAP) + 'px)';
		this.menu.setAttribute('data-placement', data.placement);
	}

	// --- Cleanup ---

	destroy_menu() {
		this.stop_positioning();
		if (this.trigger) {
			this.trigger.removeAttribute('aria-haspopup');
			this.trigger.removeAttribute('aria-controls');
			this.trigger.removeAttribute('aria-expanded');
		}
		if (this.menu && this.menu.parentNode) {
			this.menu.parentNode.removeChild(this.menu);
		}
		this.menu = null;
		this.menu_scroll = null;
		this.search_input = null;
	}
}

customElements.define('yalwa-dropdown', YalwaDropdown);